import * as layout from '../../components/markLayout.module.css';
import { MarkButton, MarkSelect, MarkLabel } from '../../components/MarkControls';
import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, Outlet } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import { MarkPageHeader, MarkEmptyState } from '../../components/MarkLayout';
import P from '@hero/ui-kit/typography/P';
import usePagerProps from '../../../../hooks/usePagerProps';
import useSegments from '../../api/useSegments';
import SegmentsList from './SegmentsList';
import LimitItems from '../../../../components/LimitItems';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import type { SegmentKind } from '../../types';
const SegmentsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [kindFilter, setKindFilter] = useState<SegmentKind | ''>('');
    const offset = Number(searchParams.get('offset') || 0);
    const limit = Number(searchParams.get('limit') || 20);
    const { data: segmentsResponse, isFetching, isLoading, error } = useSegments({ offset, limit });
    const allSegments = segmentsResponse?.data?.segments || [];
    const totalCount = segmentsResponse?.data?.total_count || 0;
    const segments = useMemo(
        () => (kindFilter ? allSegments.filter((s) => s.kind === kindFilter) : allSegments),
        [allSegments, kindFilter]
    );
    const handleKindFilterChange = useCallback(
        (kind: SegmentKind | '') => {
            setKindFilter(kind);
            if (offset > 0) {
                setSearchParams((prev) => {
                    prev.delete('offset');
                    return prev;
                });
            }
        },
        [offset, setSearchParams]
    );
    const pagerProps = usePagerProps({
        total_count: totalCount,
        limit,
        offset
    });
    return (
        <Section subtheme={['regular', 'background']} className={layout.page} noDefaultPadding>
            <MarkPageHeader
                title="Segments"
                description="Define audiences with saved rules or imported contact lists."
            >
                <MarkButton
                    size="regular"
                    variant="primary"
                    onClick={() =>
                        navigate({
                            pathname: '/mark/segments/new',
                            search: searchParams.toString()
                        })
                    }
                >
                    New segment
                </MarkButton>
                <MarkButton
                    size="regular"
                    onClick={() =>
                        navigate({
                            pathname: '/mark/segments/import',
                            search: searchParams.toString()
                        })
                    }
                >
                    Import CSV
                </MarkButton>
            </MarkPageHeader>
            <div className={layout.toolbar}>
                <LimitItems
                    isLoading={isLoading}
                    limit={limit}
                    offset={offset}
                    total_count={totalCount}
                />
                <div className={`${layout.field} ${layout.segmentKindFilter}`}>
                    <MarkLabel htmlFor="mark-segment-kind">Kind on this page</MarkLabel>
                    <MarkSelect
                        id="mark-segment-kind"
                        value={kindFilter}
                        onChange={(e) =>
                            handleKindFilterChange(e.currentTarget.value as SegmentKind | '')
                        }
                        aria-label="Filter kind on the current page"
                    >
                        <option value="">All kinds</option>
                        <option value="QUERY">Query</option>
                        <option value="IMPORTED">Imported</option>
                    </MarkSelect>
                </div>
                {kindFilter && !isLoading && (
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            color: 'var(--mark-muted)'
                        }}
                    >
                        {segments.length} {kindFilter.toLowerCase()} segment
                        {segments.length === 1 ? '' : 's'} on this page
                    </P>
                )}
            </div>
            {error ? (
                <QueryInlineError error={error} />
            ) : isLoading ? (
                <InlineLoader />
            ) : segments.length === 0 && !kindFilter ? (
                <MarkEmptyState
                    title="No segments yet"
                    description="Create a segment to choose the audience for a blast or drip, or import a contact list. Segments cannot be deleted after creation."
                />
            ) : (
                <SegmentsList
                    segments={segments}
                    isLoading={isFetching}
                    pagerProps={pagerProps}
                    noDataMessage={
                        kindFilter
                            ? `No ${kindFilter.toLowerCase()} segments on this page.`
                            : undefined
                    }
                />
            )}
            <Outlet />
        </Section>
    );
};
export default SegmentsPage;
